
import { Component, Prop, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";

import GET_ACTIVITIES from "@/apollo/projectActivities/GetActivities.gql";
import GET_DECLINED_VISIT_MATCHES from "@/apollo/declinedVisitMatches/GetDeclinedVisitMatches.gql";
import GET_BOOKMARKED_CONTRACTORS_FOR_DECLINED_VISIT from "@/apollo/smartMatch/GetBookmarkedContractorsForDeclinedVisit.gql";
import GET_PROJECT_BOOKMARKS from "@/apollo/smartMatch/GetProjectBookmarks.gql";
import GET_BOOKMARKED_CONTRACTORS_FOR_MANAGER from "@/apollo/smartMatch/GetBookmarkedContractorsForManager.gql";
import GET_BOOKMARKED_CONTRACTORS_FOR_ADMIN from "@/apollo/smartMatch/GetBookmarkedContractorsForAdmin.gql";

import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import {
  User,
  Company,
  Project,
  Project_Activity,
  Query_RootProjectArgs,
  Project_Bool_Exp,
} from "@/generated/graphql";
import DefaultContentLayout from "@/components/scaffold/DefaultContentLayoutNew.vue";
import ClientFilters from "@/components/companyProjects/ClientMasterviewFilters.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import CardBodyItem from "@/components/card/CardBodyItem.vue";
import ActivityFormModal from "@/components/projects/activities/ActivityFormModalNew.vue";
import projectsFiltersTemplate from "@/data/filtersTemplates/projectsFiltersTemplate";
import RouteNames from "@/entities/RouteNames";
import paginationConfig from "@/data/paginationConfig";
import ProjectsFiltersQuery from "@/entities/filters/ProjectsFiltersQuery";
import {
  getFiltersDataFromQuery,
  joinQueryValues,
} from "@/helpers/router/prepareFiltersQuery";
import { cloneDeep, isEqual } from "lodash-es";
import {
  getPaginationDataFromQuery,
  getPaginationQuery,
} from "@/helpers/router/preparePaginationQuery";
import Roles from "@/entities/Roles";
import PaginationConfig from "@/entities/PaginationConfig";
import moment from "moment";
import EmptyListImageProjects from "@/components/icons/emptyListImages/EmptyListImageProjects.vue";
import VisitsCardWrapper from "@/components/projects/VisitsCardWrapper.vue";

interface ProjectFilter extends Query_RootProjectArgs {}
interface DeclinedMatch {
  id: string;
  user_id: string;
  match_percentage: number;
  user: {
    id: string;
    given_name: string;
    family_name: string;
    avatar?: string;
    profile?: {
      job_title?: string;
      occupation?: string;
    };
  };
}

interface BookmarkedContractor {
  id: string;
  user_id: string;
  match_percentage: number;
  bookmarked_at: string;
  user: {
    id: string;
    given_name: string;
    family_name: string;
    avatar?: string;
    profile?: {
      job_title?: string;
      occupation?: string;
    };
  };
}
@Component({
  components: {
    VisitsCardWrapper,
    EmptyListImageProjects,
    DefaultContentLayout,
    ActivityFormModal,
    ClientFilters,
    EmptyListPlaceholder,
    CardPagination,
    LoadingOverlay,
    CardBodyItem,
  },
  apollo: {
    activities: {
      query: GET_ACTIVITIES,
      variables() {
        return this.projectFilter;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.project_activity.length;
        return data.project_activity;
      },
    },
  },
})
export default class PendingVisits extends mixins(GetRelativeRouteNameMixin) {
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Number, required: true }) readonly declinedTotal!: number;

  expandedDescriptions: { [key: string]: boolean } = {};
  descriptionMaxLength = 100;
  dialog: boolean = false;
  selectedProject?: Project = undefined;
  selectedActivity?: Project_Activity = undefined;
  suggestedAssignee?: any = undefined;

  activities: Project_Activity[] | null = null;
  allDeclinedMatches: { [key: string]: DeclinedMatch[] } = {};
  allBookmarkedContractors: { [key: string]: BookmarkedContractor[] } = {};

  paginationConfig: PaginationConfig = { ...paginationConfig };

  projectFilters: ProjectsFiltersQuery = cloneDeep(projectsFiltersTemplate);
  RouteNames: typeof RouteNames = RouteNames;

  get hasData(): boolean {
    return !this.isLoading && Boolean(this.activities);
  }

  get filtersEmpty(): boolean {
    return isEqual(this.projectFilters, projectsFiltersTemplate);
  }

  get emptyListPlaceholder(): string {
    if (this.filtersEmpty) {
      return "You currently do not have any declined visits";
    }

    return `You currently do not have any declined visits`;
  }

  get dashboardRoute() {
    return {
      name: RouteNames.DASHBOARD,
    };
  }

  get isLoading(): boolean {
    return this.$apollo.queries.activities.loading;
  }

  get empty(): boolean {
    if (!this.activities) {
      return false;
    }

    return this.activities.length === 0;
  }

  get currentUserManagerId(): string {
    return this.currentUser.manager!.id;
  }

  get projectFilter(): ProjectFilter {
    const { page, limit } = this.paginationConfig;
    const nextOffset = page * limit - limit;

    const { createdAt, title, managers, contractors, location } =
      this.projectFilters;

    const filterConditions = [];

    filterConditions.push({
      drafted: {
        _eq: false,
      },
      accepted: {
        _eq: false,
      },
      completed: {
        _eq: false,
      },
      deleted: {
        _eq: false,
      },
      project: {
        company_id: { _eq: this.company.id },
      },
    });

    if (managers.length) {
      filterConditions.push({
        manager: {
          id: { _in: managers },
        },
      });
    }

    if (contractors.length) {
      filterConditions.push({
        contractor: {
          id: {
            _in: contractors,
          },
        },
      });
    }
    if (this.isManager) {
      filterConditions.push({
        project: {
          manager: {
            id: { _eq: this.currentUserManagerId },
          },
        },
      });
    }

    if (location.length) {
      filterConditions.push({
        location: { _ilike: `%${location}%` },
      });
    }

    if (title) {
      filterConditions.push({
        project: {
          client_name: { _ilike: `%${title}%` },
        },
      });
    }

    if (createdAt) {
      const startDate = moment(createdAt).utc().startOf("day").toISOString();
      const endDate = moment(createdAt).utc().endOf("day").toISOString();

      const datesConditions = [
        { start_date: { _gte: startDate } },
        { start_date: { _lte: endDate } },
      ];

      filterConditions.push({
        _and: datesConditions,
      });
    }

    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: filterConditions,
      },
    };
  }

  get isAdmin(): boolean {
    return this.currentUser.role === Roles.ADMIN;
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get queryPropsToWatch(): string {
    return JSON.stringify([this.projectFilters, this.paginationConfig]);
  }

  get queryParams(): {
    filters: ProjectsFiltersQuery;
    pagination: PaginationConfig;
  } {
    const query = this.$route.query;

    const { start_date: startDate, end_date: endDate, ...queryParams } = query;

    const queryData = {
      startDate,
      endDate,
      ...queryParams,
    };

    const filters = getFiltersDataFromQuery<ProjectsFiltersQuery>(
      projectsFiltersTemplate,
      queryData
    );
    const pagination = {
      ...this.paginationConfig,
      ...getPaginationDataFromQuery(query),
    };

    return {
      filters,
      pagination,
    };
  }

  get isChosenPageEmpty(): boolean {
    const collectionEmpty = this.activities?.length === 0;
    const notFirstPage = this.paginationConfig.page !== 1;

    return !this.isLoading && collectionEmpty && notFirstPage;
  }

  @Watch("queryPropsToWatch")
  updateQuery(): void {
    if (this.isChosenPageEmpty) {
      return this.showFirstPage();
    }

    const paginationQuery = getPaginationQuery(this.paginationConfig);

    const { createdAt, location, ...queryParams } = this.projectFilters;

    const query = joinQueryValues({
      ...paginationQuery,
      ...queryParams,
    });

    if (isEqual(query, this.$route.query)) {
      return;
    }

    this.$router.push({ query }).catch(() => {});
  }

  @Watch("$route.query", { immediate: true })
  onQueryChange(): void {
    const { filters, pagination } = this.queryParams;

    this.projectFilters = filters;
    this.paginationConfig = pagination;
  }

  @Watch("activities")
  onActivitiesChange(): void {
    if (this.activities && this.activities.length > 0) {
      this.fetchDeclinedMatchesForActivities();
      this.fetchBookmarkedContractorsForActivities();
    }
  }

  updateFilters(filters: ProjectsFiltersQuery): void {
    const dataEqual = isEqual(filters, this.projectFilters);

    if (dataEqual) {
      return;
    }

    this.projectFilters = filters;
  }

  resetFilters(): void {
    if (isEqual(this.projectFilters, projectsFiltersTemplate)) {
      return;
    }

    this.projectFilters = cloneDeep(projectsFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

  showFirstPage(): void {
    this.paginationConfig = {
      ...this.paginationConfig,
      page: paginationConfig.page,
    };
  }

  refetchVisits(): void {
    this.$apollo.queries.activities.refetch();
    this.$emit("visitUpdated");
  }

  getDeclinedMatchesForActivity(activityId: string): DeclinedMatch[] {
    return this.allDeclinedMatches[activityId] || [];
  }

  getBookmarkedContractorsForActivity(activityId: string): BookmarkedContractor[] {
    return this.allBookmarkedContractors[activityId] || [];
  }

  async fetchDeclinedMatchesForActivities(): Promise<void> {
    if (!this.activities || this.activities.length === 0) return;

    // Fetch declined matches for each activity
    for (const activity of this.activities) {
      // Skip if current user didn't create this activity
      if (activity.created_by !== this.currentUser.id) {
        console.debug(`🚫 Skipping declined matches for activity ${activity.id} - not created by current user`);
        this.$set(this.allDeclinedMatches, activity.id, []);
        continue;
      }

      try {
        console.debug(`🔍 Fetching declined matches for activity ${activity.id}`);
        
        const result = await this.$apollo.query({
          query: GET_DECLINED_VISIT_MATCHES,
          variables: {
            project_activity_id: activity.id
          }
        });
        
        const matches = result.data.declined_visit_matches || [];
        
        this.$set(this.allDeclinedMatches, activity.id, matches);
      } catch (error) {
        console.error(`Error fetching declined matches for activity ${activity.id}:`, error);
        this.$set(this.allDeclinedMatches, activity.id, []);
      }
    }
  }

  async fetchBookmarkedContractorsForActivities(): Promise<void> {
    if (!this.activities || this.activities.length === 0) return;

    // Fetch bookmarked contractors for each activity
    for (const activity of this.activities) {
      // Skip if current user didn't create this activity
      if (activity.created_by !== this.currentUser.id) {
        console.debug(`🚫 Skipping bookmarked contractors for activity ${activity.id} - not created by current user`);
        this.$set(this.allBookmarkedContractors, activity.id, []);
        continue;
      }
      // Build variables outside try-catch so it's accessible in catch block
      const variables = {
        project_id: activity.project_id,
        manager_id: this.isManager && this.currentUser.manager?.id ? this.currentUser.manager.id : null,
        admin_id: this.isAdmin && this.currentUser.admin?.id ? this.currentUser.admin.id : null,
        exclude_user_id: activity.contractor?.user_id || null
      };

      try {
        
        // Skip if no manager_id or admin_id is available
        if (!variables.manager_id && !variables.admin_id) {
          console.debug(`🔖 Skipping bookmark query for activity ${activity.id} - no manager_id or admin_id available`);
          this.$set(this.allBookmarkedContractors, activity.id, []);
          continue;
        }
        
        
        // Use role-specific queries to avoid null UUID issues
        let result;
        if (this.isManager && variables.manager_id) {
          console.debug(`🔖 Using manager-specific bookmark query`);
          result = await this.$apollo.query({
            query: GET_BOOKMARKED_CONTRACTORS_FOR_MANAGER,
            variables: {
              manager_id: variables.manager_id,
              project_id: variables.project_id
            }
          });
        } else if (this.isAdmin && variables.admin_id) {
          console.debug(`🔖 Using admin-specific bookmark query`);
          result = await this.$apollo.query({
            query: GET_BOOKMARKED_CONTRACTORS_FOR_ADMIN,
            variables: {
              admin_id: variables.admin_id,
              project_id: variables.project_id
            }
          });
        } else {
          console.debug(`🔖 No valid manager_id or admin_id, skipping bookmark query`);
          this.$set(this.allBookmarkedContractors, activity.id, []);
          continue;
        }
        
        
        const bookmarks = result.data.smart_match_bookmark || [];
        
        this.$set(this.allBookmarkedContractors, activity.id, bookmarks);
      } catch (error) {
        console.error(`❌ Error fetching bookmarked contractors for activity ${activity.id}:`);
        console.log('Full error object:', error);
        this.$set(this.allBookmarkedContractors, activity.id, []);
      }
    }
  }

  handleOpenActivityModal(eventData: { projectActivity: Project_Activity; suggestedAssignee: any }): void {
    this.selectedActivity = eventData.projectActivity;
    this.suggestedAssignee = eventData.suggestedAssignee;
    this.dialog = true;
  }

  closeActivityModal(): void {
    this.dialog = false;
    this.selectedActivity = undefined;
    this.suggestedAssignee = undefined;
  }
}
